import { createContext, useContext, ReactNode, useMemo } from 'react';
import { useSegmentFields } from '../hooks/QueryBuilder/useSegmentFields';
import { useAvailableGroups, AvailableGroup } from '../hooks/QueryBuilder/useAvailableGroups';
import { QueryField } from '../types/QueryBuilder';

export type { AvailableGroup };

interface QueryBuilderDataContextType {
    fields: QueryField[];
    loadingFields: boolean;
    errorFields: string | null;
    categories: string[];
    simpleFields: QueryField[];
    groups: AvailableGroup[];
    loadingGroups: boolean;
    refetchGroups: () => Promise<void>;
}

const QueryBuilderDataContext = createContext<QueryBuilderDataContextType | undefined>(undefined);

export const QueryBuilderDataProvider = ({ children }: { children: ReactNode }) => {
    const { fields, loading: loadingFields, error: errorFields, categories, simpleFields } = useSegmentFields();
    const { groups, loading: loadingGroups, refetch: refetchGroups } = useAvailableGroups();

    const value = useMemo<QueryBuilderDataContextType>(() => ({
        fields,
        loadingFields,
        errorFields,
        categories,
        simpleFields,
        groups,
        loadingGroups,
        refetchGroups,
    }), [fields, loadingFields, errorFields, categories, simpleFields, groups, loadingGroups, refetchGroups]);

    return <QueryBuilderDataContext.Provider value={value}>{children}</QueryBuilderDataContext.Provider>;
};

export const useQueryBuilderData = (): QueryBuilderDataContextType => {
    const context = useContext(QueryBuilderDataContext);
    if (!context) throw new Error('useQueryBuilderData must be used within a QueryBuilderDataProvider');
    return context;
};
